import { Component, HostListener, OnInit } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { RelatorioListaProdutosParams } from '../../../models/relatorios/relatorio-lista-produtos.model';
import { RelatorioListaProdutosService } from '../../../services/relatorios/relatorio-lista-produtos.service';

// Serviços de apoio
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { PessoaDTO } from '../../../models/clientes/pessoa.model';
import { GrupoDTO } from '../../../models/grupo.model';
import { MarcaDTO } from '../../../models/marca.model';
import { SubGrupoDTO } from '../../../models/subgrupo.model';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { FileUtils } from '../../../shared/utils/file-utils';

@Component({
  selector: 'app-relatorio-produtos',
  templateUrl: './relatorio-produtos.component.html',
  styleUrls: ['./relatorio-produtos.component.scss']
})
export class RelatorioProdutosComponent implements OnInit {

  // Filtros de busca
  searchNome: string = '';
  loadingGrupos: boolean = false;
  loadingSubGrupos: boolean = false;
  loadingMarcas: boolean = false;
  loadingFornecedores: boolean = false;

  totalRecords: number = 0;
  first3: number = 0;

  // Parâmetros do relatório
  params: RelatorioListaProdutosParams = {
    grupoId: undefined,
    subGrupoId: undefined,
    marcaId: undefined,
    fornecedorId: undefined,
    saldo: 'TODOS',  // Valor padrão (pode ser 'POSITIVO', 'NEGATIVO' ou 'TODOS')
    exibirCusto: true,   // NOVO — default true (regressão zero)
    exibirPreco: true,   // NOVO — default true (regressão zero)
    exibirSaldo: true    // NOVO — default true (regressão zero)
  };

  // Listas para popular os selects
  grupos: any[] = [];
  selectedGrupo: GrupoDTO | null = null;
  subgrupos: any[] = [];
  selectedSubGrupo: SubGrupoDTO | null = null;
  marcas: any[] = [];
  selectedMarca: MarcaDTO | null = null;
  fornecedores: any[] = [];
  selectedFornecedor: PessoaDTO | null = null;

  emitindoRelatorio: boolean = false;

  constructor(
    private relatorioService: RelatorioListaProdutosService,
    private messageService: MessageService,
    private pessoaService: PessoaService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.findFornecedores();
  }

  onGruposFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingGrupos = true;
    this.loadingGrupos = false;
  }

  findFornecedores(page: number = 0, sort: string = 'id,asc') {
    const size = 10;
    let filter = '';

    filter += `&email=${this.searchNome}`;
    filter += `&nome=${this.searchNome}`;
    filter += `&fantasia=${this.searchNome}`;
    filter += `&ruc=${this.searchNome}`;
    filter += `&telefone=${this.searchNome}`;
    filter += `&ci=${this.searchNome}`;
    filter += `&nomeCidade=${this.searchNome}`;

    if (!isNaN(Number(this.searchNome))) {
      filter += `&codigoPessoa=${this.searchNome}`;
    }
    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: `categoriaFornecedor=true${filter ? `&${filter}` : ''}`,
      })
      .subscribe(
        (response: any) => {
          this.fornecedores = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
        },
        (error: any) => {
          console.error('Erro ao buscar fornecedores:', error);
          this.fornecedores = [];
          this.totalRecords = 0;
        }
      );
  }

  onFornecedorFilter(event: any) {
    this.searchNome = event.filter?.trim() || '';
    this.loadingFornecedores = true;
    this.findFornecedores();
    this.loadingFornecedores = false;
  }

  // Emite o relatório
  emitirRelatorio(): void {
    this.emitindoRelatorio = true;

    // atualizar os parâmetros com os valores selecionados
    // (atribuir undefined quando o filtro foi desmarcado para limpar o estado anterior)
    this.params.grupoId = this.selectedGrupo?.id ?? undefined;
    this.params.subGrupoId = this.selectedSubGrupo?.id ?? undefined;
    this.params.marcaId = this.selectedMarca?.id ?? undefined;
    this.params.fornecedorId = this.selectedFornecedor?.id ?? undefined;
    if (!this.params.saldo) {
      this.params.saldo = 'TODOS';
    }
    this.relatorioService.emitirRelatorio(this.params).subscribe({
      next: (response: HttpResponse<string>) => {
        const fallback =
          response.headers.get('X-Relatorio-Fallback-Aplicado') === 'true';

        if (fallback) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant(
              'relatorios.toasts.lista_itens_fallback_template.summary'
            ),
            detail: this.translate.instant(
              'relatorios.toasts.lista_itens_fallback_template.detail'
            ),
          });
        }

        const base64Pdf = response.body ?? '';
        FileUtils.abrirPdf(base64Pdf, 'relatorio-produtos.pdf');
        this.emitindoRelatorio = false;
      },
      error: (err) => {
        console.error('Erro ao gerar relatório', err);
        this.emitindoRelatorio = false;

        if (err?.status === 404) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.aviso'),
            detail: this.translate.instant('relatorios.mensagens.nenhum_dado_encontrado'),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: this.translate.instant('relatorios.mensagens.erro_gerar_relatorio'),
          });
        }
      },
    });
  }

  // Abre o PDF em outra aba
  private abrirPdfEmNovaAba(base64: string): void {
    const byteCharacters = atob(base64);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = base64.charCodeAt(i);
    }
    const blob = new Blob([byteNumbers], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.key === 'Enter' && !this.emitindoRelatorio) {
      event.preventDefault();
      this.emitirRelatorio();
    }
  }
}
